import { Component } from '@theme/component';

/**
 * Configurator: sums the price of the checked model radio plus any checked
 * upgrade/accessory checkboxes and renders a running total everywhere
 * data-configurator-total appears (inline total row + bottom bar).
 * Every buy button sets the cart to the selection (see #syncCart); options
 * are added only when they have a real Shopify product (data-variant-id).
 */
class ProductConfigurator extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.updateTotal();
    const section = this.closest('.shopify-section') || document;
    section.addEventListener('submit', this.#onFormSubmit, true);
    section.addEventListener('click', this.#onShopNowClick, true);
  }

  updateTotal = () => {
    const inputs = this.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
    let total = 0;

    inputs.forEach((input) => {
      total += Number(input.dataset.price || 0);
    });

    const currency = this.dataset.currency || '$';
    const formatted = currency + total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    this.querySelectorAll('[data-configurator-total]').forEach((el) => {
      el.textContent = formatted;
    });

    this.#updateMediaFromSelection();
  };

  /**
   * Swaps the product's currently visible gallery photo to match the
   * selected model's thumbnail (set via the "Thumbnail image" option on
   * each model's configurator option). Only radio options (the "Model"
   * group) can be exclusively selected like this, so whichever checked
   * radio carries a data-media-image wins. No-op if that option has no
   * thumbnail set, leaving the gallery's real product photo untouched.
   */
  #updateMediaFromSelection = () => {
    const checked = this.querySelector('input[type="radio"]:checked[data-media-image]');
    const url = checked?.dataset.mediaImage;
    if (!url) return;

    const gallery = this.closest('.shopify-section')?.querySelector('media-gallery');
    if (!gallery) return;

    const img =
      gallery.querySelector('slideshow-slide[aria-hidden="false"] img.product-media__image') ||
      gallery.querySelector('img.product-media__image');
    if (!(img instanceof HTMLImageElement) || img.src === url) return;

    img.srcset = '';
    img.sizes = '';
    img.src = url;
  };

  /**
   * Sets the cart to exactly what is selected: the bike (at the quantity chosen
   * on the page, not stacked on top of earlier clicks) plus every ticked
   * upgrade/accessory that has a real Shopify product; unticked ones are removed.
   * @param {'cart' | 'checkout'} destination
   */
  #syncCart = async (destination) => {
    if (this.busy) return;
    const section = this.closest('.shopify-section') || document;
    const bikeId = section.querySelector('product-form-component input[name="id"]')?.value;
    if (!bikeId) return;
    const qtyInput = section.querySelector('product-form-component input[name="quantity"]');
    const qty = Math.max(1, Number(qtyInput?.value) || 1);

    const updates = { [bikeId]: qty };
    const missing = [];
    this.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const id = input.dataset.variantId;
      if (id) updates[id] = input.checked ? qty : 0;
      else if (input.checked) missing.push(input.dataset.label || 'An option');
    });

    if (
      missing.length &&
      !window.confirm(
        missing.join(', ') +
          (missing.length > 1 ? ' are' : ' is') +
          ' not yet available to buy online and will not be added to the cart. Continue?'
      )
    ) {
      return;
    }

    this.busy = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        window.alert(err.description || err.message || 'This item could not be added to the cart.');
        return;
      }
      window.location.href = root + destination;
    } finally {
      this.busy = false;
    }
  };

  /** Configurator bar button. */
  addToCart = () => this.#syncCart('cart');

  /** The page's Add to cart button: same full selection instead of the bike alone. */
  #onFormSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#syncCart('cart');
  };

  /** Shop now (Buy it now): same selection, straight to checkout. */
  #onShopNowClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.shopify-payment-button__button--unbranded, shopify-buy-it-now-button')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#syncCart('checkout');
  };
}

if (!customElements.get('product-configurator')) {
  customElements.define('product-configurator', ProductConfigurator);
}
